/**
 * LocalDatabase - IndexedDB wrapper using Dexie.js
 * 
 * Platform-agnostic local database for offline-first architecture.
 * Web: Dexie.js (IndexedDB)
 * Future RN: Swap for WatermelonDB/SQLite
 */

import Dexie, { type Table } from 'dexie';

/**
 * Schema version contract
 * ------------------------
 * Every Dexie schema change must:
 *   1. Add a NEW `this.version(N).stores({...})` block — never edit an existing one.
 *   2. Bump `CURRENT_DB_VERSION` to N.
 *   3. Add an `.upgrade()` callback ONLY when row shape needs rewriting.
 *      Index-only additions need no upgrade callback (Dexie preserves data).
 *   4. Never remove a store or rename a primary key — both silently drop rows.
 *
 * On any failure to open (corruption, downgrade, blocked upgrade) we run
 * `rescueQueuedRowsFromRawIDB()` BEFORE any destructive recovery, so queued
 * offline expenses/income are preserved into localStorage and re-imported
 * on the next successful open.
 */
const CURRENT_DB_VERSION = 3;
const QUEUE_RESCUE_KEY = 'wren:dexie-queue-rescue';

export interface LocalExpense {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  merchant: string;
  category: string;
  note: string | null;
  expense_date: string;
  tag: string | null;
  synced: boolean;
  created_at: string;
  updated_at: string;
  amount_eur?: number | null;
  fx_rate?: number | null;
  fx_source?: string | null;
  fx_date?: string | null;
  /** Dirty = modified locally, needs push to server */
  _dirty: boolean;
  /** Soft-deleted locally, pending server delete */
  _deleted: boolean;
}

export interface LocalIncome {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  source: string;
  category: string | null;
  income_date: string;
  is_recurring: boolean;
  recurrence_interval: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
  amount_eur?: number | null;
  fx_rate?: number | null;
  fx_source?: string | null;
  fx_date?: string | null;
  _dirty: boolean;
  _deleted: boolean;
}

export interface SyncMeta {
  id: string; // 'expenses' | 'income'
  lastPullCursor: string; // ISO timestamp of last pulled updated_at
  lastPushAt: string;
}

/**
 * Local-only log of sync conflicts. When the server has a newer version of a
 * row than the local dirty copy, the syncEngine accepts server-wins and writes
 * an entry here so the user can audit what was overwritten.
 */
export interface SyncConflict {
  id: string;                 // uuid
  user_id: string;
  table: 'expenses' | 'income';
  row_id: string;
  detected_at: string;        // ISO
  server_updated_at: string;
  local_updated_at: string;
  local_snapshot: string;     // JSON of the local copy that was overwritten
  reviewed: boolean;
}

export interface CachedBudgetSettings {
  user_id: string;
  monthly_budget: number;
  currency: string;
  updated_at: string;
}

type ExpenseServerRow = {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  merchant: string;
  category: string;
  note: string | null;
  expense_date: string;
  tag: string | null;
  synced?: boolean;
  created_at: string;
  updated_at: string;
  amount_eur?: number | null;
  fx_rate?: number | null;
  fx_source?: string | null;
  fx_date?: string | null;
};

type IncomeServerRow = {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  source: string;
  category: string | null;
  income_date: string;
  is_recurring: boolean;
  recurrence_interval: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
  amount_eur?: number | null;
  fx_rate?: number | null;
  fx_source?: string | null;
  fx_date?: string | null;
};

const expenseCompositeKey = (row: Pick<LocalExpense | ExpenseServerRow, 'expense_date' | 'amount' | 'merchant'>) =>
  `${row.expense_date}|${Number(row.amount).toFixed(2)}|${(row.merchant || '').trim().toLowerCase()}`;

const incomeCompositeKey = (row: Pick<LocalIncome | IncomeServerRow, 'income_date' | 'amount' | 'source'>) =>
  `${row.income_date}|${Number(row.amount).toFixed(2)}|${(row.source || '').trim().toLowerCase()}`;

class WrenDatabase extends Dexie {
  expenses!: Table<LocalExpense, string>;
  income!: Table<LocalIncome, string>;
  syncMeta!: Table<SyncMeta, string>;
  budgetSettings!: Table<CachedBudgetSettings, string>;
  syncConflicts!: Table<SyncConflict, string>;

  constructor() {
    super('wren-local');

    this.version(1).stores({
      expenses: 'id, user_id, expense_date, updated_at, [user_id+expense_date], [user_id+_dirty]',
      income: 'id, user_id, income_date, updated_at, [user_id+income_date], [user_id+_dirty]',
      syncMeta: 'id',
    });

    this.version(2).stores({
      expenses: 'id, user_id, expense_date, updated_at, [user_id+expense_date], [user_id+_dirty]',
      income: 'id, user_id, income_date, updated_at, [user_id+income_date], [user_id+_dirty]',
      syncMeta: 'id',
      budgetSettings: 'user_id',
    });

    this.version(3).stores({
      expenses: 'id, user_id, expense_date, updated_at, [user_id+expense_date], [user_id+_dirty]',
      income: 'id, user_id, income_date, updated_at, [user_id+income_date], [user_id+_dirty]',
      syncMeta: 'id',
      budgetSettings: 'user_id',
      syncConflicts: 'id, user_id, detected_at, reviewed, [user_id+reviewed]',
    });

    // Block: another tab is holding an older version open. Don't wipe — log + wait.
    this.on('blocked', () => {
      console.warn('[localDatabase] open blocked by another tab holding an older version');
    });

    // Another tab upgraded the schema. Close this connection cleanly so the
    // other tab can finish; we'll reopen on next access.
    this.on('versionchange', (event) => {
      console.warn('[localDatabase] versionchange detected, closing', event);
      this.close();
    });
  }
}

// Singleton instance
const db = new WrenDatabase();

/**
 * Snapshot every queued (dirty) or pending-delete row directly from raw IDB
 * BEFORE Dexie attempts an upgrade or recovery. Survives schema corruption,
 * downgrades, and accidental `Dexie.delete()`. Written to localStorage so it
 * outlives the IDB instance.
 */
async function rescueQueuedRowsFromRawIDB(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  try {
    const dbs = await (indexedDB.databases?.() ?? Promise.resolve([]));
    if (!dbs.find((d) => d.name === 'wren-local')) return;
  } catch {
    // Some browsers (Firefox) don't expose databases(). Fall through and try anyway.
  }

  const open = indexedDB.open('wren-local');
  const raw = await new Promise<IDBDatabase | null>((resolve) => {
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => resolve(null);
    open.onblocked = () => resolve(null);
    // Don't trigger upgradeneeded here — open at whatever version exists.
  }).catch(() => null);
  if (!raw) return;

  try {
    const rescue: { expenses: unknown[]; income: unknown[]; savedAt: string } = {
      expenses: [],
      income: [],
      savedAt: new Date().toISOString(),
    };
    const storeNames = Array.from(raw.objectStoreNames);
    const wanted = ['expenses', 'income'].filter((n) => storeNames.includes(n));
    if (wanted.length === 0) {
      raw.close();
      return;
    }
    const tx = raw.transaction(wanted, 'readonly');
    await Promise.all(
      wanted.map(
        (name) =>
          new Promise<void>((resolve) => {
            const req = tx.objectStore(name).getAll();
            req.onsuccess = () => {
              const rows = (req.result as Array<Record<string, unknown>>) || [];
              const queued = rows.filter((r) => r && (r._dirty === true || r._dirty === 1 || r._deleted === true));
              (rescue as unknown as Record<string, unknown[]>)[name] = queued;
              resolve();
            };
            req.onerror = () => resolve();
          })
      )
    );
    raw.close();

    const hasAny = rescue.expenses.length + rescue.income.length > 0;
    if (!hasAny) return;

    // Merge with any prior rescue payload so we never overwrite older queued items.
    try {
      const prior = localStorage.getItem(QUEUE_RESCUE_KEY);
      if (prior) {
        const parsed = JSON.parse(prior) as typeof rescue;
        const byId = (list: unknown[]) => {
          const map = new Map<string, unknown>();
          for (const row of list) {
            const id = (row as { id?: string }).id;
            if (id) map.set(id, row);
          }
          return map;
        };
        const mergeLists = (a: unknown[], b: unknown[]) => {
          const map = byId(a);
          for (const [id, row] of byId(b)) if (!map.has(id)) map.set(id, row);
          return Array.from(map.values());
        };
        rescue.expenses = mergeLists(rescue.expenses, parsed.expenses || []);
        rescue.income = mergeLists(rescue.income, parsed.income || []);
      }
    } catch {
      /* ignore parse errors, keep fresh rescue */
    }

    localStorage.setItem(QUEUE_RESCUE_KEY, JSON.stringify(rescue));
    console.warn(
      `[localDatabase] rescued ${rescue.expenses.length} expenses + ${rescue.income.length} income rows to localStorage`
    );
  } catch (err) {
    console.warn('[localDatabase] rescue failed', err);
  }
}

/**
 * Restore any queued rows previously written by rescueQueuedRowsFromRawIDB().
 * Runs once after a successful open. Uses bulkPut, preserving _dirty so the
 * sync engine will push them.
 */
async function restoreRescuedQueue(): Promise<void> {
  const raw = localStorage.getItem(QUEUE_RESCUE_KEY);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as { expenses?: LocalExpense[]; income?: LocalIncome[] };
    const expenses = (parsed.expenses || []).filter((r) => r && r.id);
    const income = (parsed.income || []).filter((r) => r && r.id);
    if (expenses.length) {
      // Don't clobber a row that already exists in the live DB with a newer updated_at.
      const existing = await db.expenses.bulkGet(expenses.map((e) => e.id));
      const toPut = expenses.filter((row, i) => {
        const cur = existing[i];
        if (!cur) return true;
        return (row.updated_at || '') > (cur.updated_at || '');
      });
      if (toPut.length) await db.expenses.bulkPut(toPut);
    }
    if (income.length) {
      const existing = await db.income.bulkGet(income.map((e) => e.id));
      const toPut = income.filter((row, i) => {
        const cur = existing[i];
        if (!cur) return true;
        return (row.updated_at || '') > (cur.updated_at || '');
      });
      if (toPut.length) await db.income.bulkPut(toPut);
    }
    localStorage.removeItem(QUEUE_RESCUE_KEY);
    if (expenses.length || income.length) {
      console.log(`[localDatabase] restored ${expenses.length} expenses + ${income.length} income from rescue payload`);
    }
  } catch (err) {
    console.warn('[localDatabase] restoreRescuedQueue failed — leaving payload for retry', err);
  }
}

/**
 * Open Dexie with a safety net: snapshot queued rows first, then open.
 * If the upgrade throws (VersionError, UpgradeError, etc.), we DO NOT delete
 * the database — we keep the rescue payload around and surface the error so
 * the next session has a chance to recover.
 */
export const dbReady: Promise<void> = (async () => {
  await rescueQueuedRowsFromRawIDB();
  try {
    await db.open();
    if (db.verno !== CURRENT_DB_VERSION) {
      console.warn(
        `[localDatabase] opened at version ${db.verno}, expected ${CURRENT_DB_VERSION}`
      );
    }
    await restoreRescuedQueue();
  } catch (err) {
    console.error('[localDatabase] open failed — queued rows preserved in localStorage', err);
    throw err;
  }
})();

// One-shot rehydration from legacy `cashee-local` IndexedDB (captured by runLegacyMigration).
// Runs once per session; safe no-op if no payload present.
(async () => {
  try {
    await dbReady;
    const raw = localStorage.getItem('wren:legacy-idb-payload');
    if (!raw) return;
    const data = JSON.parse(raw) as Record<string, unknown[]>;
    for (const [storeName, rows] of Object.entries(data)) {
      if (!Array.isArray(rows) || rows.length === 0) continue;
      const table = (db as unknown as Record<string, { bulkPut?: (rows: unknown[]) => Promise<unknown> }>)[storeName];
      if (table && typeof table.bulkPut === 'function') {
        try { await table.bulkPut(rows); } catch (err) {
          console.warn(`[localDatabase] rehydrate ${storeName} failed`, err);
        }
      }
    }
    localStorage.removeItem('wren:legacy-idb-payload');
    console.log('[localDatabase] rehydrated from legacy cashee-local');
  } catch (err) {
    console.warn('[localDatabase] legacy rehydrate failed', err);
  }
})();

export const localDatabase = {
  // ─── Expenses ───────────────────────────────────────────

  async putExpense(expense: Omit<LocalExpense, '_dirty' | '_deleted'>): Promise<void> {
    await db.expenses.put({
      ...expense,
      _dirty: true,
      _deleted: false,
    });
  },

  async getExpenses(userId: string, monthStart: string, monthEnd: string): Promise<LocalExpense[]> {
    return db.expenses
      .where('[user_id+expense_date]')
      .between([userId, monthStart], [userId, monthEnd], true, true)
      .filter(e => !e._deleted)
      .toArray();
  },

  async getRecentExpenses(userId: string, limit: number = 7): Promise<LocalExpense[]> {
    const all = await db.expenses
      .where('user_id')
      .equals(userId)
      .filter(e => !e._deleted)
      .toArray();
    // Sort by expense_date desc, then created_at desc
    all.sort((a, b) => {
      const dateCmp = b.expense_date.localeCompare(a.expense_date);
      if (dateCmp !== 0) return dateCmp;
      return b.created_at.localeCompare(a.created_at);
    });
    return all.slice(0, limit);
  },

  async getExpenseById(id: string): Promise<LocalExpense | undefined> {
    return db.expenses.get(id);
  },

  async deleteExpense(id: string): Promise<void> {
    const existing = await db.expenses.get(id);
    if (existing) {
      // Mark as dirty + deleted so sync engine pushes the delete
      await db.expenses.update(id, { _dirty: true, _deleted: true, updated_at: new Date().toISOString() });
    }
  },

  async updateExpense(id: string, changes: Partial<LocalExpense>): Promise<void> {
    await db.expenses.update(id, {
      ...changes,
      _dirty: true,
      updated_at: new Date().toISOString(),
    });
  },

  async getUnsyncedExpenses(userId: string): Promise<LocalExpense[]> {
    // NOTE: We intentionally scan by user_id and filter in memory instead of
    // querying the [user_id+_dirty] compound index. Dexie does not
    // consistently coerce booleans to 0/1 across versions/browsers, so
    // `.equals([userId, 1])` silently returned zero rows even when writes
    // stored `_dirty: true`. That broke ALL server sync (nothing pushed for
    // weeks — bulk receipts and single expenses alike). Scanning by userId
    // is O(user's rows), which is fine for our data volumes.
    return db.expenses
      .where('user_id')
      .equals(userId)
      .filter(e => e._dirty === true || (e._dirty as unknown as number) === 1 || e._deleted === true)
      .toArray();
  },

  async bulkPutExpenses(expenses: LocalExpense[]): Promise<void> {
    await db.expenses.bulkPut(expenses);
  },

  async markExpenseSynced(id: string): Promise<void> {
    await db.expenses.update(id, { _dirty: false, synced: true });
  },

  async removeExpensePermanently(id: string): Promise<void> {
    await db.expenses.delete(id);
  },

  /**
   * Purge local expenses that duplicate server records by composite key
   * (user_id + expense_date + amount + merchant) but have a different id.
   *
   * This fixes a class of bugs where re-imports / sync-id mismatches leave
   * orphan local copies that get re-merged into the UI as "unsynced", causing
   * doubled totals. Only removes records that are NOT _dirty and NOT _deleted,
   * so we never lose unsynced user data.
   *
   * Returns the number of rows removed.
   */
  async purgeDuplicateExpenses(
    userId: string,
    serverRows: Array<{ id: string; expense_date: string; amount: number; merchant: string }>
  ): Promise<number> {
    if (!serverRows.length) return 0;
    const serverIds = new Set(serverRows.map(r => r.id));
    const serverKeys = new Set(
      serverRows.map(r => `${r.expense_date}|${Number(r.amount).toFixed(2)}|${(r.merchant || '').trim().toLowerCase()}`)
    );

    const locals = await db.expenses.where('user_id').equals(userId).toArray();
    const orphanIds: string[] = [];
    for (const e of locals) {
      if (e._dirty || e._deleted) continue; // never touch unsynced or pending-delete rows
      if (serverIds.has(e.id)) continue;     // already matched by id
      const key = `${e.expense_date}|${Number(e.amount).toFixed(2)}|${(e.merchant || '').trim().toLowerCase()}`;
      if (serverKeys.has(key)) orphanIds.push(e.id);
    }
    if (orphanIds.length) {
      await db.expenses.bulkDelete(orphanIds);
    }
    return orphanIds.length;
  },

  /**
   * Aggressive one-shot cleaner: removes ALL non-dirty local expenses for a
   * user that don't have a matching server id. This is safe because the
   * caller guarantees `serverIdsAllTime` is the full set of server-side ids
   * for that user. Used as a recovery path when composite-key matching is
   * insufficient (e.g. local copies have slightly different merchant text
   * after re-import, leading to phantom duplicates).
   *
   * Returns the number of rows removed.
   */
  async hardSyncExpenses(userId: string, serverIdsAllTime: string[]): Promise<number> {
    const serverIdSet = new Set(serverIdsAllTime);
    const locals = await db.expenses.where('user_id').equals(userId).toArray();
    const orphanIds: string[] = [];
    for (const e of locals) {
      if (e._dirty || e._deleted) continue; // preserve unsynced / pending-delete
      if (!serverIdSet.has(e.id)) orphanIds.push(e.id);
    }
    if (orphanIds.length) await db.expenses.bulkDelete(orphanIds);
    return orphanIds.length;
  },

  async hardSyncIncome(userId: string, serverIdsAllTime: string[]): Promise<number> {
    const serverIdSet = new Set(serverIdsAllTime);
    const locals = await db.income.where('user_id').equals(userId).toArray();
    const orphanIds: string[] = [];
    for (const i of locals) {
      if (i._dirty || i._deleted) continue;
      if (!serverIdSet.has(i.id)) orphanIds.push(i.id);
    }
    if (orphanIds.length) await db.income.bulkDelete(orphanIds);
    return orphanIds.length;
  },

  /**
   * One-shot recovery for legacy duplicate rows where the local orphan has a
   * different id than the server copy. If the orphan is dirty, rewrite it onto
   * the canonical server id so future sync updates the real server row instead
   * of keeping a phantom duplicate forever.
   */
  async reconcileExpensesWithServer(userId: string, serverRows: ExpenseServerRow[]): Promise<number> {
    if (!serverRows.length) return 0;

    const serverById = new Map(serverRows.map((row) => [row.id, row]));
    const serverByKey = new Map(serverRows.map((row) => [expenseCompositeKey(row), row]));
    const locals = await db.expenses.where('user_id').equals(userId).toArray();

    let changed = 0;

    await db.transaction('rw', db.expenses, async () => {
      for (const local of locals) {
        if (serverById.has(local.id)) continue;

        const matchingServer = serverByKey.get(expenseCompositeKey(local));
        if (matchingServer) {
          if (local._deleted || !local._dirty) {
            await db.expenses.delete(local.id);
            changed++;
            continue;
          }

          await db.expenses.put({
            id: matchingServer.id,
            user_id: matchingServer.user_id,
            amount: local.amount,
            currency: local.currency,
            merchant: local.merchant,
            category: local.category,
            note: local.note,
            expense_date: local.expense_date,
            tag: local.tag,
            synced: false,
            created_at: matchingServer.created_at || local.created_at,
            updated_at: local.updated_at,
            _dirty: true,
            _deleted: false,
          });
          await db.expenses.delete(local.id);
          changed++;
          continue;
        }

        if (!local._dirty && !local._deleted) {
          await db.expenses.delete(local.id);
          changed++;
        }
      }
    });

    return changed;
  },

  async reconcileIncomeWithServer(userId: string, serverRows: IncomeServerRow[]): Promise<number> {
    if (!serverRows.length) return 0;

    const serverById = new Map(serverRows.map((row) => [row.id, row]));
    const serverByKey = new Map(serverRows.map((row) => [incomeCompositeKey(row), row]));
    const locals = await db.income.where('user_id').equals(userId).toArray();

    let changed = 0;

    await db.transaction('rw', db.income, async () => {
      for (const local of locals) {
        if (serverById.has(local.id)) continue;

        const matchingServer = serverByKey.get(incomeCompositeKey(local));
        if (matchingServer) {
          if (local._deleted || !local._dirty) {
            await db.income.delete(local.id);
            changed++;
            continue;
          }

          await db.income.put({
            id: matchingServer.id,
            user_id: matchingServer.user_id,
            amount: local.amount,
            currency: local.currency,
            source: local.source,
            category: local.category,
            income_date: local.income_date,
            is_recurring: local.is_recurring,
            recurrence_interval: local.recurrence_interval,
            note: local.note,
            created_at: matchingServer.created_at || local.created_at,
            updated_at: local.updated_at,
            _dirty: true,
            _deleted: false,
          });
          await db.income.delete(local.id);
          changed++;
          continue;
        }

        if (!local._dirty && !local._deleted) {
          await db.income.delete(local.id);
          changed++;
        }
      }
    });

    return changed;
  },

  // ─── Income ─────────────────────────────────────────────

  async putIncome(income: Omit<LocalIncome, '_dirty' | '_deleted'>): Promise<void> {
    await db.income.put({
      ...income,
      _dirty: true,
      _deleted: false,
    });
  },

  async getIncome(userId: string, monthStart: string, monthEnd: string): Promise<LocalIncome[]> {
    return db.income
      .where('[user_id+income_date]')
      .between([userId, monthStart], [userId, monthEnd], true, true)
      .filter(i => !i._deleted)
      .toArray();
  },

  async deleteIncome(id: string): Promise<void> {
    const existing = await db.income.get(id);
    if (existing) {
      await db.income.update(id, { _dirty: true, _deleted: true, updated_at: new Date().toISOString() });
    }
  },

  async updateIncome(id: string, changes: Partial<LocalIncome>): Promise<void> {
    await db.income.update(id, {
      ...changes,
      _dirty: true,
      updated_at: new Date().toISOString(),
    });
  },

  async getUnsyncedIncome(userId: string): Promise<LocalIncome[]> {
    // See getUnsyncedExpenses for why we scan+filter instead of using the
    // [user_id+_dirty] compound index.
    return db.income
      .where('user_id')
      .equals(userId)
      .filter(i => i._dirty === true || (i._dirty as unknown as number) === 1 || i._deleted === true)
      .toArray();
  },

  async bulkPutIncome(income: LocalIncome[]): Promise<void> {
    await db.income.bulkPut(income);
  },

  async markIncomeSynced(id: string): Promise<void> {
    await db.income.update(id, { _dirty: false });
  },

  async removeIncomePermanently(id: string): Promise<void> {
    await db.income.delete(id);
  },

  /**
   * Same idea as purgeDuplicateExpenses, but for income.
   * Composite key: user_id + income_date + amount + source.
   */
  async purgeDuplicateIncome(
    userId: string,
    serverRows: Array<{ id: string; income_date: string; amount: number; source: string }>
  ): Promise<number> {
    if (!serverRows.length) return 0;
    const serverIds = new Set(serverRows.map(r => r.id));
    const serverKeys = new Set(
      serverRows.map(r => `${r.income_date}|${Number(r.amount).toFixed(2)}|${(r.source || '').trim().toLowerCase()}`)
    );

    const locals = await db.income.where('user_id').equals(userId).toArray();
    const orphanIds: string[] = [];
    for (const i of locals) {
      if (i._dirty || i._deleted) continue;
      if (serverIds.has(i.id)) continue;
      const key = `${i.income_date}|${Number(i.amount).toFixed(2)}|${(i.source || '').trim().toLowerCase()}`;
      if (serverKeys.has(key)) orphanIds.push(i.id);
    }
    if (orphanIds.length) {
      await db.income.bulkDelete(orphanIds);
    }
    return orphanIds.length;
  },

  // ─── Sync Meta ──────────────────────────────────────────

  async getSyncMeta(table: string): Promise<SyncMeta | undefined> {
    return db.syncMeta.get(table);
  },

  async setSyncMeta(meta: SyncMeta): Promise<void> {
    await db.syncMeta.put(meta);
  },

  // ─── Utilities ──────────────────────────────────────────

  // ─── Budget Settings Cache ───────────────────────────────

  async getCachedBudgetSettings(userId: string): Promise<CachedBudgetSettings | undefined> {
    return db.budgetSettings.get(userId);
  },

  async setCachedBudgetSettings(settings: CachedBudgetSettings): Promise<void> {
    await db.budgetSettings.put(settings);
  },

  async clearAll(): Promise<void> {
    await Promise.all([
      db.expenses.clear(),
      db.income.clear(),
      db.syncMeta.clear(),
      db.budgetSettings.clear(),
    ]);
  },

  async requestPersistentStorage(): Promise<boolean> {
    if (navigator.storage?.persist) {
      return navigator.storage.persist();
    }
    return false;
  },

  /** Migrate old localStorage queue items into IndexedDB */
  async migrateFromLocalStorage(): Promise<number> {
    const QUEUE_KEY = 'wren_expense_queue';
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return 0;

    try {
      const items = JSON.parse(raw) as Array<{
        action: string;
        data: Record<string, unknown>;
        table?: string;
      }>;

      let migrated = 0;
      for (const item of items) {
        if (item.action === 'insert' && item.data.user_id) {
          const table = (item.table || 'expenses') as 'expenses' | 'income';
          const now = new Date().toISOString();

          if (table === 'expenses') {
            await db.expenses.put({
              id: (item.data.id as string) || crypto.randomUUID(),
              user_id: item.data.user_id as string,
              amount: Number(item.data.amount),
              currency: (item.data.currency as string) || 'EUR',
              merchant: (item.data.merchant as string) || '',
              category: (item.data.category as string) || 'Bills/Other',
              note: (item.data.note as string) || null,
              expense_date: (item.data.expense_date as string) || now.split('T')[0],
              tag: (item.data.tag as string) || null,
              synced: false,
              created_at: now,
              updated_at: now,
              _dirty: true,
              _deleted: false,
            });
            migrated++;
          } else if (table === 'income') {
            await db.income.put({
              id: (item.data.id as string) || crypto.randomUUID(),
              user_id: item.data.user_id as string,
              amount: Number(item.data.amount),
              currency: (item.data.currency as string) || 'EUR',
              source: (item.data.source as string) || '',
              category: (item.data.category as string) || null,
              income_date: (item.data.income_date as string) || now.split('T')[0],
              is_recurring: false,
              recurrence_interval: null,
              note: (item.data.note as string) || null,
              created_at: now,
              updated_at: now,
              _dirty: true,
              _deleted: false,
            });
            migrated++;
          }
        }
      }

      // Clear old queue after successful migration
      if (migrated > 0) {
        localStorage.removeItem(QUEUE_KEY);
        console.log(`[LocalDB] Migrated ${migrated} items from localStorage`);
      }

      return migrated;
    } catch (err) {
      console.error('[LocalDB] Migration from localStorage failed:', err);
      return 0;
    }
  },

  // ─── Sync Conflicts ─────────────────────────────────────

  async logSyncConflict(entry: Omit<SyncConflict, 'id' | 'detected_at' | 'reviewed'>): Promise<void> {
    await db.syncConflicts.put({
      ...entry,
      id: crypto.randomUUID(),
      detected_at: new Date().toISOString(),
      reviewed: false,
    });
  },

  async getUnreviewedConflicts(userId: string): Promise<SyncConflict[]> {
    const all = await db.syncConflicts.where('user_id').equals(userId).toArray();
    return all.filter(c => !c.reviewed).sort((a, b) => b.detected_at.localeCompare(a.detected_at));
  },

  async getAllConflicts(userId: string, limit = 100): Promise<SyncConflict[]> {
    const all = await db.syncConflicts.where('user_id').equals(userId).toArray();
    return all.sort((a, b) => b.detected_at.localeCompare(a.detected_at)).slice(0, limit);
  },

  async markConflictReviewed(id: string): Promise<void> {
    await db.syncConflicts.update(id, { reviewed: true });
  },

  async markAllConflictsReviewed(userId: string): Promise<void> {
    const unreviewed = await db.syncConflicts.where('user_id').equals(userId).toArray();
    await db.syncConflicts.bulkPut(unreviewed.map(c => ({ ...c, reviewed: true })));
  },
};

export default db;
